import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { 
  ArrowLeft, XCircle, Calendar, Building2, User, Phone,
  IndianRupee, RefreshCw, ChevronLeft, ChevronRight, Search,
  FileText, AlertTriangle, Clock, Download
} from 'lucide-react';
import { toast } from 'sonner';

const CancellationHistory = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState(null);
  const [cancellations, setCancellations] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [city, setCity] = useState('');
  const [cities, setCities] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Detail dialog
  const [selectedCancellation, setSelectedCancellation] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const fetchCancellationHistory = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (city) params.city = city;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await api.getCancellationHistory(params);
      setData(response.data.summary);
      setCancellations(response.data.cancellations || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching cancellation history:', error);
      toast.error('Failed to load cancellation history');
    } finally {
      setLoading(false);
    }
  }, [page, city, startDate, endDate]);

  const fetchCities = useCallback(async () => {
    try {
      const response = await api.getCities();
      setCities(response.data || []);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  }, []);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  useEffect(() => {
    fetchCancellationHistory();
  }, [fetchCancellationHistory]);

  // Filter cancellations by search term (client-side)
  const filteredCancellations = cancellations.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (c.customer_name || '').toLowerCase().includes(term) ||
      (c.project_name || '').toLowerCase().includes(term) ||
      (c.booking_id || '').toLowerCase().includes(term) ||
      (c.cancelled_by_name || '').toLowerCase().includes(term)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return `₹${Number(value).toLocaleString('en-IN')} L`;
  };

  const handleViewDetail = (cancellation) => {
    setSelectedCancellation(cancellation);
    setShowDetailDialog(true);
  };

  const handleResetFilters = () => {
    setCity('');
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
    setPage(1);
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const params = {};
      if (city) params.city = city;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await api.exportCancellationHistory(params);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'cancellation_history.csv';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export cancellation history');
    } finally {
      setExporting(false);
    }
  };

  // Check if user is admin
  const isAdmin = ['city_admin', 'strategy_support_admin', 'super_admin', 'crm_support'].includes(user?.role);
  const isCityAdmin = user?.role === 'city_admin';
  const isCrmSupport = user?.role === 'crm_support';

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-gray-600">Only admins can view cancellation history.</p>
          <Button onClick={() => navigate(-1)} className="mt-4">
            Go Back
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <XCircle className="w-6 h-6 text-red-500" />
                  Cancellation History
                </h1>
                <p className="text-sm text-gray-500">
                  Audit trail of all cancelled bookings
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={exporting || loading || total === 0}
                data-testid="export-csv-button"
              >
                <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchCancellationHistory}
                disabled={loading}
                data-testid="refresh-button"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-5 bg-red-50 border-red-200">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 rounded-lg">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-red-700">Total Cancelled Bookings</p>
                  <p className="text-2xl font-bold text-red-800" data-testid="total-cancelled">
                    {data.total_cancelled_bookings || 0}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="p-5 bg-orange-50 border-orange-200">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <IndianRupee className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-orange-700">Total Cancelled Value</p>
                  <p className="text-2xl font-bold text-orange-800" data-testid="total-cancelled-av">
                    {formatCurrency(data.total_cancelled_av_lacs)}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Customer, project, booking ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* City Filter */}
            {!isCityAdmin && (
              <div className="w-[180px]">
                <label className="text-sm font-medium text-gray-700 mb-1 block">City</label>
                <Select value={city || "all"} onValueChange={(val) => setCity(val === "all" ? "" : val)}>
                  <SelectTrigger data-testid="city-filter">
                    <SelectValue placeholder="All Cities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cities</SelectItem>
                    {cities.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Date Range */}
            <div className="w-[160px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">From Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-filter"
              />
            </div>
            <div className="w-[160px]">
              <label className="text-sm font-medium text-gray-700 mb-1 block">To Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date-filter"
              />
            </div>

            {/* Reset */}
            <Button
              variant="outline"
              onClick={handleResetFilters}
              data-testid="reset-filters-button"
            >
              Reset
            </Button>
          </div>
        </Card>

        {/* Cancellation List */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">
                Cancellations ({total})
              </h2>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                    data-testid="prev-page-button"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}
                    data-testid="next-page-button"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Loading cancellation history...</p>
            </div>
          ) : filteredCancellations.length === 0 ? (
            <div className="p-8 text-center">
              <XCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No cancelled bookings found</p>
              {(city || startDate || endDate || searchTerm) && (
                <Button
                  variant="link"
                  onClick={handleResetFilters}
                  className="mt-2"
                >
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {filteredCancellations.map((cancellation, idx) => (
                <div
                  key={cancellation.booking_id || idx}
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleViewDetail(cancellation)}
                  data-testid={`cancellation-row-${cancellation.booking_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900 truncate">
                          {cancellation.customer_name}
                        </span>
                        <Badge variant="destructive" className="text-xs">
                          Cancelled
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {cancellation.project_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <IndianRupee className="w-3.5 h-3.5" />
                          {formatCurrency(cancellation.agreement_value_lacs)}
                        </span>
                        {cancellation.city && (
                          <span className="text-gray-400">
                            {cancellation.city}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 p-2 bg-red-50 rounded-md border border-red-100">
                        <p className="text-sm text-red-800 font-medium">
                          Reason: {cancellation.cancellation_reason || 'Not specified'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-sm shrink-0">
                      <p className="text-gray-500 flex items-center gap-1 justify-end">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate(cancellation.cancelled_at)}
                      </p>
                      <p className="text-gray-600 mt-1">
                        By: <span className="font-medium">{cancellation.cancelled_by_name}</span>
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Cancellation Details
            </DialogTitle>
            <DialogDescription>
              Full audit trail for this cancelled booking
            </DialogDescription>
          </DialogHeader>
          
          {selectedCancellation && (
            <div className="space-y-4 mt-4">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase">Customer</label>
                  <p className="font-medium">{selectedCancellation.customer_name}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Phone</label>
                  <p className="font-medium">{selectedCancellation.customer_phone || '-'}</p>
                </div>
              </div>

              {/* Booking Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase">Project</label>
                  <p className="font-medium">{selectedCancellation.project_name}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Unit</label>
                  <p className="font-medium">{selectedCancellation.unit_number || '-'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase">Agreement Value</label>
                  <p className="font-medium text-lg">
                    {formatCurrency(selectedCancellation.agreement_value_lacs)}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">City</label>
                  <p className="font-medium">{selectedCancellation.city || '-'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 uppercase">Booking Date</label>
                  <p className="font-medium">{formatDate(selectedCancellation.booking_date)}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 uppercase">Sales Rep</label>
                  <p className="font-medium">{selectedCancellation.rep_name || '-'}</p>
                </div>
              </div>

              {/* Cancellation Info */}
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Cancellation Information
                </h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Reason</label>
                    <p className="font-medium text-red-800 bg-red-50 p-2 rounded mt-1">
                      {selectedCancellation.cancellation_reason || 'Not specified'}
                    </p>
                  </div>

                  {selectedCancellation.cancellation_notes && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Additional Notes</label>
                      <p className="text-gray-700 bg-gray-50 p-2 rounded mt-1">
                        {selectedCancellation.cancellation_notes}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Cancelled By</label>
                      <p className="font-medium">{selectedCancellation.cancelled_by_name}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Cancelled At</label>
                      <p className="font-medium">{formatDate(selectedCancellation.cancelled_at)}</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 uppercase">Previous Status</label>
                    <Badge variant="outline" className="mt-1">
                      {selectedCancellation.previous_status || 'Unknown'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Booking ID */}
              <div className="border-t pt-4 mt-4">
                <label className="text-xs text-gray-500 uppercase">Booking ID</label>
                <p className="font-mono text-sm text-gray-600">{selectedCancellation.booking_id}</p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowDetailDialog(false)}
                  data-testid="close-detail-dialog"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setShowDetailDialog(false);
                    navigate(`/bookings/${selectedCancellation.booking_id}`);
                  }}
                  data-testid="view-booking-button"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Booking
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CancellationHistory;
